import type { ReactNode } from 'react';
import { Button } from './Button';
import { Popover, type Ancre } from './Popover';
import { t } from '../../i18n';

/**
 * Feuille d'actions.
 *
 * Elle montait du bas de l'ecran. Elle sort maintenant du bouton qui
 * l'ouvre, dans la meme fenetre que les filtres d'evenements — meme
 * bordure, meme ombre, meme apparition. Une feuille qui monte du bas
 * repond en bas a une question posee en haut : on perd de vue ce qu'on
 * vient de toucher, et la reponse s'affiche loin de la demande.
 *
 * Le nom reste : c'est bien une feuille d'actions, un choix parmi
 * plusieurs. Seule sa facon de paraitre a change, et les ecrans n'ont
 * rien eu a apprendre — ils passent l'ancre quand ils en ont une.
 */
export function Sheet({
  open,
  onClose,
  title,
  anchor,
  large = false,
  children,
}: {
  open: boolean;
  onClose: () => void;
  /** Petit titre gris au-dessus des actions : "Post de Mike.28". */
  title?: string;
  /**
   * Le bouton d'ou elle sort. Sans lui, elle parait au milieu de
   * l'ecran — c'est le cas de ce qui s'ouvre autrement qu'en touchant
   * quelque chose de precis.
   */
  anchor?: Ancre;
  /** Pour ce qui n'est pas un menu : un formulaire, un billet, un scanner. */
  large?: boolean;
  children: ReactNode;
}) {
  return (
    <Popover
      open={open}
      anchor={anchor}
      onClose={onClose}
      label={title ?? t.commun.actions}
      large={large}
    >
      {title && <p className="sheet__title">{title}</p>}
      {children}
    </Popover>
  );
}

/**
 * Ligne d'action d'une feuille : une icone et un libelle.
 *
 * Une pastille, comme les categories de la fenetre de filtres — c'est
 * la meme fenetre, elle ne peut pas parler deux langues.
 */
export function SheetAction({
  icon,
  children,
  onClick,
  tone = 'default',
  className = '',
}: {
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
  /** `danger` pour les actions destructrices (supprimer). */
  tone?: 'default' | 'danger';
  /** De quoi habiller l'action autrement, la ou la feuille le demande. */
  className?: string;
}) {
  return (
    <button
      className={`sheet__action sheet__action--${tone} ${className}`.trim()}
      onClick={onClick}
    >
      {icon}
      <span className="sheet__action__label">{children}</span>
    </button>
  );
}

/**
 * Bouton de bas de feuille.
 *
 * Il n'y a plus de bouton "Annuler" : un appui a cote de la fenetre la
 * referme, et un bouton qui ne fait que ca prenait une ligne pour rien.
 * Ne reste donc que l'affirmatif, et il n'apparait que lorsqu'il y a
 * quelque chose a valider.
 *
 * C'est le bouton de l'application, celui-la meme qui applique les
 * filtres : plus de style a part pour la meme fonction.
 */
export function SheetConfirm({
  onClick,
  label = t.commun.confirmer,
}: {
  onClick: () => void;
  label?: string;
}) {
  return (
    <Button variant="primary" block className="sheet__confirm" onClick={onClick}>
      {label}
    </Button>
  );
}
